import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Nav } from "../components/Nav";
import { toast } from "sonner";
import { Bell, CalendarDays, Trash2, Heart, Loader2 } from "lucide-react";

const OCCASIONS = ["Birthday", "Anniversary", "Wedding", "Graduation", "Other"];
const EMPTY = { event_name: "", recipient_name: "", event_date: "", occasion_type: "Birthday", remind_3_days: true, remind_on_day: true };

export default function Reminders() {
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/reminders").then(({ data }) => setReminders(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) load();
    else if (!authLoading) setLoading(false);
  }, [user, authLoading]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/reminders", form);
      setReminders((prev) => [...prev, data].sort((a, b) => a.event_date.localeCompare(b.event_date)));
      setForm(EMPTY);
      toast.success("Reminder saved!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reminders/${id}`);
      setReminders((prev) => prev.filter((r) => r.id !== id));
      toast.success("Reminder deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const fmtDate = (d) => {
    try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return d; }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-4xl mx-auto px-5 py-10">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#C8B6FF] border-2 border-[#0A0A0A] shadow-[3px_3px_0px_#0A0A0A]">
            <CalendarDays className="w-6 h-6" />
          </span>
          <div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold" data-testid="reminders-heading">Occasion Planner</h1>
            <p className="text-[#4A4A4A]">Never miss a birthday or big day. Save it once, we'll remind you — free.</p>
          </div>
        </div>

        {!user ? (
          <div className="nb-card p-10 text-center mt-10" data-testid="reminders-login-prompt">
            <Bell className="w-8 h-8 mx-auto mb-3" />
            <h2 className="text-lg font-display font-bold">Log in to plan your occasions</h2>
            <p className="text-[#4A4A4A] mt-1 mb-5">Create a free account to save reminders and get a nudge before every event.</p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/login" data-testid="reminders-login-btn" className="nb-btn bg-white px-5 py-2">Log in</Link>
              <Link to="/signup" data-testid="reminders-signup-btn" className="nb-btn bg-[#FFB5A7] px-5 py-2">Sign up free</Link>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="nb-card p-6 mt-8 grid gap-4" data-testid="reminder-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="font-bold text-sm">Event name / description</span>
                  <input
                    data-testid="reminder-event-name"
                    required value={form.event_name}
                    onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                    placeholder="Mom's 60th birthday"
                    className="mt-1 w-full px-4 py-2.5 rounded-xl border-2 border-[#0A0A0A] bg-white focus:outline-none focus:ring-2 focus:ring-[#C8B6FF]"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-sm">Recipient name</span>
                  <input
                    data-testid="reminder-recipient-name"
                    value={form.recipient_name}
                    onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                    placeholder="Mom"
                    className="mt-1 w-full px-4 py-2.5 rounded-xl border-2 border-[#0A0A0A] bg-white focus:outline-none focus:ring-2 focus:ring-[#C8B6FF]"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-sm">Event date</span>
                  <input
                    data-testid="reminder-event-date"
                    type="date" required value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    className="mt-1 w-full px-4 py-2.5 rounded-xl border-2 border-[#0A0A0A] bg-white focus:outline-none focus:ring-2 focus:ring-[#C8B6FF]"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-sm">Occasion type</span>
                  <select
                    data-testid="reminder-occasion-type"
                    value={form.occasion_type}
                    onChange={(e) => setForm({ ...form, occasion_type: e.target.value })}
                    className="mt-1 w-full px-4 py-2.5 rounded-xl border-2 border-[#0A0A0A] bg-white focus:outline-none focus:ring-2 focus:ring-[#C8B6FF]"
                  >
                    {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              </div>

              <div className="flex flex-wrap gap-5">
                <label className="flex items-center gap-2 font-semibold">
                  <input
                    data-testid="reminder-remind-3-days"
                    type="checkbox" checked={form.remind_3_days}
                    onChange={(e) => setForm({ ...form, remind_3_days: e.target.checked })}
                    className="w-5 h-5 accent-[#C8B6FF]"
                  />
                  Remind 3 days before
                </label>
                <label className="flex items-center gap-2 font-semibold">
                  <input
                    data-testid="reminder-remind-on-day"
                    type="checkbox" checked={form.remind_on_day}
                    onChange={(e) => setForm({ ...form, remind_on_day: e.target.checked })}
                    className="w-5 h-5 accent-[#C8B6FF]"
                  />
                  Remind on the day
                </label>
              </div>

              <button data-testid="reminder-submit" disabled={saving} className="nb-btn bg-[#C8B6FF] px-6 py-2.5 justify-self-start disabled:opacity-60">
                {saving ? "Saving…" : "Save reminder"}
              </button>
            </form>

            <h2 className="text-xl font-display font-bold mt-10 mb-4">Your reminders</h2>
            {loading ? (
              <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : reminders.length === 0 ? (
              <div className="nb-card p-8 text-center text-[#4A4A4A]" data-testid="reminders-empty">No reminders yet. Add your first occasion above.</div>
            ) : (
              <div className="nb-card p-0 overflow-hidden" data-testid="reminders-list">
                {reminders.map((r, i) => (
                  <div key={r.id} data-testid={`reminder-row-${r.id}`} className={`flex items-center gap-4 p-4 ${i > 0 ? "border-t-2 border-[#0A0A0A]" : ""}`}>
                    <div className="grid place-items-center w-11 h-11 rounded-xl bg-[#F9F871] border-2 border-[#0A0A0A] font-bold text-xs text-center leading-tight shrink-0">
                      {r.occasion_type.slice(0, 4)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold truncate">{r.event_name}</div>
                      <div className="text-sm text-[#4A4A4A]">
                        {fmtDate(r.event_date)}{r.recipient_name ? ` · for ${r.recipient_name}` : ""}
                      </div>
                      <div className="text-xs text-[#4A4A4A] mt-0.5">
                        {[r.remind_3_days && "3 days before", r.remind_on_day && "on the day"].filter(Boolean).join(" · ") || "No reminders set"}
                      </div>
                    </div>
                    <button
                      data-testid={`reminder-delete-${r.id}`}
                      onClick={() => remove(r.id)}
                      className="nb-btn bg-white w-10 h-10 p-0 shrink-0" title="Delete reminder"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <footer className="border-t-2 border-[#0A0A0A] py-8 text-center text-sm text-[#4A4A4A]">
        SendOccasions — made with <Heart className="w-4 h-4 inline" /> · Sending greetings is always free.
      </footer>
    </div>
  );
}
